import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useSubmitSurvey, useRaynetConnection } from '@/hooks/useRaynet';
import { useAvailableRaynetUsers } from '@/hooks/useRaynetUsers';
import { useFormPersistence } from '@/hooks/useFormPersistence';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToastNotifications } from '@/components/ui/toast-notification';
import { SidePanel } from '@/components/ui/side-panel';
import { SalesRepSelector } from '@/components/ui/sales-rep-selector';
import { Users, FileText, Target, Settings, ArrowLeft, ArrowRight, Save, Home, Eye, UserCheck } from 'lucide-react';
import FormStepOne from './components/FormStepOne';
import FormStepTwo from './components/FormStepTwo';
import FormStepThree from './components/FormStepThree';
import FormStepFour from './components/FormStepFour';
import FormSummary from './components/FormSummary';
import { DealerSurveyData } from '@/api/types';
import { useTranslation } from 'react-i18next';
import { isValidEmail, isValidContactName } from '@/utils/validation';

export function PublicForm() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useRaynetConnection();
  // useSubmitForm(); // Starý hook - ponecháme pro zpětnou kompatibilitu
  const submitSurveyMutation = useSubmitSurvey(); // Nový hook pro dotazníky
  const { user } = useAuth();
  const { isAdminAuthenticated } = useAdminAuth();
  const { data: raynetUsersResponse } = useAvailableRaynetUsers();
  
  // Check if we're in admin mode (admin auth required + adminMode param or just admin auth)
  // Temporarily allow adminMode=true parameter for testing, even without admin auth
  const isAdminMode = searchParams.get('adminMode') === 'true' || isAdminAuthenticated;
  
  // Helper function to get user's full name from username/email
  const getUserFullName = useCallback((username: string) => {
    if (!raynetUsersResponse?.data) return username;
    const user = raynetUsersResponse.data.find(u => u.username === username);
    return user ? user.full_name : username;
  }, [raynetUsersResponse]);
  
  // Debug logging
  useEffect(() => {
    console.log('🔍 Admin mode debug:', {
      isAdminAuthenticated,
      adminModeParam: searchParams.get('adminMode'),
      isAdminMode,
      user: user?.username
    });
  }, [isAdminAuthenticated, searchParams, isAdminMode, user?.username]);
  
  // Form persistence hook (with admin mode consideration)
  const {
    isSaving,
    lastSaved,
    isFormSaved,
    saveForm,
    loadCompleteForm,
    markAsSubmitted,
  } = useFormPersistence(isAdminMode);

  // Toast notifications
  const { addNotification, removeNotification } = useToastNotifications();
  
  // Side panel state
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  // Form data state with proper default values
  const [formData, setFormData] = useState<DealerSurveyData>({
    companyName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    showroomStreet: '',
    showroomCity: '',
    showroomZipCode: '',
    showroomCountry: 'CZ', // Default to Czech Republic
    visitDate: '',
    salesRep: user?.username || '',
    salesRepPersonId: user?.person_id,
    designImpression: '',
    cooperationInterest: '',
    brandReaction: '',
    interests: [],
    nextSteps: [],
    otherStep: '',
    comments: '',
    whatAttracted: '',
    whatMissing: '',
    competitors: [],
    pricePerception: '',
    regionPotential: '',
    showroomInterest: '',
    showroomColorPreference: '',
    marketingMaterials: '',
    expectedSupport: [],
    otherSupport: '',
    salesMethod: [],
    maxDeliveryTime: '',
    typicalCustomer: '',
    totalStovesPerYear: '',
    designStovesPerYear: '',
    ico: '',
    selectedRaynetCompany: undefined,
    selectedRaynetAddress: undefined,
    selectedRaynetContactPerson: undefined,
    selectedAresCompany: undefined,
    source: '',
  });

  // Current step state (0-indexed)
  const [currentStep, setCurrentStep] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Form status and view mode
  const [formStatus, setFormStatus] = useState<string>('draft');
  const isReadOnlyMode = formStatus !== 'draft' && !isAdminMode;

  // Navigation function
  const goToStep = (stepIndex: number) => {
    if (stepIndex < 0 || stepIndex > 4) return;
    
    // Povolit přechod na Shrnutí pouze po uložení
    if (stepIndex === 4 && !isFormSaved) {
      return;
    }
    
    setCurrentStep(stepIndex);
  };

  // Auto-save timeout reference
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear auto-save timeout on component unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Show notification helper
  const showNotification = useCallback((type: 'save' | 'submit' | 'error', message: string) => {
    const toastType = type === 'save' ? 'success' : type === 'submit' ? 'success' : 'error';
    const duration = type === 'save' ? 3000 : type === 'submit' ? 5000 : 5000;
    
    addNotification(toastType, message, undefined, duration);
  }, [addNotification]);

  // Funkce pro uložení formuláře
  const handleSaveForm = async () => {
    try {
      await saveForm(formData, 'draft');
      console.log('✅ Formulář byl uložen do databáze');
      showNotification('save', t('notifications.saved'));
    } catch (error) {
      console.error('❌ Chyba při ukládání formuláře:', error);
      showNotification('error', t('errors.server'));
    }
  };

  // Funkce pro auto-save s debounce
  const scheduleAutoSave = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      handleSaveForm();
    }, 2000); // Auto-save po 2 sekundách nečinnosti
  };

  // Auto-save při změně formData (pouze při skutečných změnách uživatele)
  const lastFormDataRef = useRef<string>('');
  useEffect(() => {
    // Skip auto-save during initialization, admin mode, or read-only mode
    if (!isInitialized || isAdminMode || isReadOnlyMode || !formData.companyName?.trim()) {
      return;
    }
    
    // Create a stable string representation of form data
    const currentFormData = JSON.stringify(formData);
    
    // Skip if this is the same as the last saved state (prevents auto-save after loading)
    if (lastFormDataRef.current === '' || lastFormDataRef.current === currentFormData) {
      lastFormDataRef.current = currentFormData;
      return;
    }
    
    // Update reference and schedule save for actual changes
    lastFormDataRef.current = currentFormData;
    console.log('⏰ Scheduling auto-save for user changes...');
    scheduleAutoSave();
  }, [formData, isInitialized, isAdminMode, isReadOnlyMode, scheduleAutoSave]);

  // Toast notifikace pro auto-save
  const [savingToastId, setSavingToastId] = useState<string | null>(null);
  
  useEffect(() => {
    if (isSaving && !savingToastId) {
      // Zobrazíme persistent notifikaci a uložíme její ID
      const toastId = addNotification('info', 'Ukládání...', undefined, 0, true);
      setSavingToastId(toastId);
    } else if (!isSaving && savingToastId) {
      // Auto-save dokončen - pouze zavřeme saving notifikaci
      // Success notifikace se zobrazí z handleSaveForm
      removeNotification(savingToastId);
      setSavingToastId(null);
    }
  }, [isSaving, savingToastId, addNotification, removeNotification]);

  // Initialize form and check for existing form to load (only once)
  const initializeRef = useRef(false);
  useEffect(() => {
    if (initializeRef.current) return; // Prevent re-initialization
    
    const initializeForm = async () => {
      try {
        // Check if user is logged in
        if (!user?.username) {
          console.log('❌ Uživatel není přihlášen');
          navigate('/login');
          return;
        }

        initializeRef.current = true; // Mark as initialized

        // Set default sales rep to logged-in user
        setFormData(prev => ({
          ...prev,
          salesRep: user.username
        }));

        // Admin mode is already initialized in useState

        // Check if there's a formId in URL to load
        const formId = searchParams.get('formId');
        if (formId) {
          console.log('📋 Načítání existujícího formuláře:', formId);
          try {
            const completeForm = await loadCompleteForm(formId);
            if (completeForm) {
              console.log('✅ Formulář načten:', formId, 'status:', completeForm.status);
              setFormData(completeForm.step_data);
              setFormStatus(completeForm.status);
            } else {
              console.log('❌ Formulář nenalezen:', formId);
              addNotification('error', 'Formulář nebyl nalezen', undefined, 5000);
            }
          } catch (error) {
            console.error('❌ Chyba při načítání formuláře:', error);
            addNotification('error', 'Chyba při načítání formuláře', undefined, 5000);
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('❌ Chyba při inicializaci formuláře:', error);
        addNotification('error', 'Chyba při inicializaci formuláře', undefined, 5000);
      }
    };

    if (user?.username) {
      initializeForm();
    }
  }, [user?.username, user?.full_name, navigate, searchParams, loadCompleteForm, addNotification]);

  // Form data update function
  const updateFormData = useCallback((updates: Partial<DealerSurveyData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  // Navigation functions
  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Submit to Raynet function
  const handleSubmitToRaynet = useCallback(() => {
    if (!formData.salesRep) {
      addNotification('error', 'Vyberte prosím obchodního zástupce', undefined, 5000);
      return;
    }

    // Create submission data with admin information if in admin mode
    const submissionData = {
      surveyData: formData,
      representative: formData.salesRep,
      ...(isAdminMode && user && {
        adminSubmission: {
          adminUser: user.username,
          adminName: user.full_name,
          submittedOnBehalf: getUserFullName(formData.salesRep)
        }
      })
    };

    submitSurveyMutation.mutate(
      submissionData,
      {
        onSuccess: async (result) => {
          console.log('✅ Dotazník úspěšně odeslán do Raynetu:', result);
          const successMessage = isAdminMode 
            ? `Dotazník byl úspěšně odeslán do Raynetu jménem uživatele: ${getUserFullName(formData.salesRep)}`
            : 'Dotazník byl úspěšně odeslán do Raynetu!';
          addNotification('success', successMessage, undefined, 8000);
          
          // Označit formulář jako odeslaný v databázi
          await markAsSubmitted(formData, result.data?.contactPersonId, result.data?.companyId);
          
          // Přesměrování na dashboard po úspěšném odeslání s parametrem
          navigate('/dashboard?raynetSubmitted=true');
        },
        onError: (error: any) => {
          console.error('❌ Chyba při odesílání do Raynetu:', error);
          const errorMessage = error?.message || 'Došlo k chybě při odesílání do Raynetu';
          addNotification('error', errorMessage, undefined, 8000);
        }
      }
    );
  }, [formData, submitSurveyMutation, addNotification, navigate, markAsSubmitted, isAdminMode, user]);

  // Step components
  const steps = [
    {
      title: t('form.step_1_title'),
      icon: Users,
      component: FormStepOne
    },
    {
      title: t('form.step_2_title'),
      icon: Target,
      component: FormStepThree
    },
    {
      title: t('form.step_3_title'),
      icon: Settings,
      component: FormStepFour
    },
    {
      title: t('form.step_4_title'),
      icon: FileText,
      component: FormStepTwo
    },
    {
      title: t('form.step_5_title'),
      icon: FileText,
      component: FormSummary
    }
  ];

  // Progress calculation
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Validation for proceeding to next step - using imported validation functions

  const canProceed = useCallback(() => {
    if (currentStep === 0) {
      const requiredFields: (keyof DealerSurveyData)[] = ['companyName', 'contactName', 'contactPhone', 'contactEmail', 'showroomStreet', 'showroomCity', 'showroomZipCode', 'showroomCountry', 'visitDate'];
      
      const result = requiredFields.every(field => {
        const value = formData[field];
        let isValid: boolean;
        
        if (field === 'contactName') {
          isValid = isValidContactName(value as string);
        } else if (field === 'contactEmail') {
          isValid = isValidEmail(value as string);
        } else {
          // Handle both string and number values - convert to string for validation
          const stringValue = String(value || '').trim();
          isValid = Boolean(value !== null && value !== undefined && stringValue !== '');
        }
        
        if (!isValid) {
          const stringValue = String(value || '').trim();
          console.log(`🔍 Pole '${field}' není validní:`, { 
            value, 
            type: typeof value, 
            trimmed: stringValue,
            isContactName: field === 'contactName',
            contactNameParts: field === 'contactName' && typeof value === 'string' ? value.trim().split(' ').filter(part => part.length > 0) : []
          });
        }
        return isValid;
      });
      
      // Automaticky zajistit, aby salesRep bylo vždy vyplněné (není v requiredFields, ale musí být pro Raynet)
      if (!formData.salesRep && user) {
        updateFormData({ 
          salesRep: user.username,
          salesRepPersonId: user.person_id
        });
      }
      
      console.log('🔍 Step 0 canProceed result:', result);
      console.log('🔍 Form data check:', {
        companyName: formData.companyName,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        showroomStreet: formData.showroomStreet,
        showroomCity: formData.showroomCity,
        showroomZipCode: formData.showroomZipCode,
        showroomCountry: formData.showroomCountry,
        visitDate: formData.visitDate,
        salesRep: formData.salesRep || user?.username,
        salesRepPersonId: formData.salesRepPersonId || user?.person_id
      });
      
      return result;
    }
    return true;
  }, [currentStep, formData, user?.full_name, updateFormData]);

  // Admin mode banner
  const AdminModeBanner = () => {
    if (!isAdminMode) return null;
    
    return (
      <div className="bg-orange-600 text-white p-3">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <span className="text-sm font-medium">{t('form.admin_mode')}</span>
            </div>
            <span className="text-orange-100 text-xs">{t('form.admin_mode_description')}</span>
          </div>
          
          {/* User selector for admin */}
          <div className="flex items-center gap-3">
            <UserCheck className="h-4 w-4" />
            <span className="text-sm font-medium min-w-0 flex-shrink-0">Vyplnit jménem:</span>
            <div className="max-w-xs">
              <SalesRepSelector
                value={formData.salesRep}
                onChange={(value, personId) => updateFormData({ 
                  salesRep: value,
                  salesRepPersonId: personId
                })}
                placeholder="Vyberte obchodníka..."
                className="text-slate-900 bg-white border-orange-300 hover:border-orange-400 text-sm"
                disabled={false}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AdminModeBanner />
      
      {/* Navigation Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              {t('form.dashboard')}
            </Button>
            <div className="text-sm text-slate-600">
              {user?.full_name}
            </div>
          </div>
        </div>
      </div>

      {/* Simplified Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 mb-2">
                {t('form.form_title')}
                {isReadOnlyMode && (
                  <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <Eye className="h-3 w-3 mr-1" />
                    Pouze prohlížení
                  </span>
                )}
              </h1>
              <p className="text-slate-600 text-sm">
                {isReadOnlyMode 
                  ? `Formulář ve stavu: ${formStatus === 'submitted' ? 'Odeslaný' : formStatus === 'synced' ? 'Synchronizovaný' : formStatus}`
                  : t('form.progress', { percentage: Math.round(progress) })
                }
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-600 mb-1">
                {t('form.step_title', { current: currentStep + 1, total: steps.length })}
              </div>
              <Progress value={progress} className="w-32" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            {/* Step indicator */}
            <div className="flex items-center justify-between mb-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;
                const isAccessible = index <= currentStep || isFormSaved;

                return (
                  <div
                    key={index}
                    className={`flex flex-col items-center cursor-pointer transition-all duration-200 ${
                      isAccessible ? 'hover:opacity-80' : 'cursor-not-allowed'
                    }`}
                    onClick={() => isAccessible && goToStep(index)}
                  >
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all duration-200 ${
                        isActive
                          ? 'bg-orange-600 text-white shadow-lg'
                          : isCompleted
                          ? 'bg-green-500 text-white'
                          : isAccessible
                          ? 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                          : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      <Icon size={20} />
                    </div>
                    <span
                      className={`text-xs text-center max-w-20 leading-tight ${
                        isActive
                          ? 'text-orange-600 font-medium'
                          : isCompleted
                          ? 'text-green-600'
                          : isAccessible
                          ? 'text-slate-600'
                          : 'text-slate-400'
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Step content */}
            <div className="min-h-96">
              {(() => {
                const StepComponent = steps[currentStep].component;
                // Handle different prop requirements for different components
                if (currentStep === 4) {
                  // FormSummary needs onSubmit and isSubmitting
                  return (
                    <StepComponent
                      formData={formData}
                      updateFormData={isReadOnlyMode ? () => {} : updateFormData}
                      onSubmit={handleSubmitToRaynet}
                      isSubmitting={submitSurveyMutation.isPending}
                      readOnly={isReadOnlyMode}
                    />
                  );
                } else {
                  // Other form steps
                  return (
                    <StepComponent
                      formData={formData}
                      updateFormData={isReadOnlyMode ? () => {} : updateFormData}
                      onSubmit={() => {}}
                      isSubmitting={false}
                      readOnly={isReadOnlyMode}
                    />
                  );
                }
              })()}
            </div>

            {/* Navigation buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between mt-8 pt-6 border-t border-slate-200">
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="w-full sm:w-auto border-slate-300 hover:bg-slate-50 order-2 sm:order-1"
              >
                <ArrowLeft size={16} className="mr-2" />
                {t('form.previous')}
              </Button>
              
              {/* Read-only mode: only navigation buttons */}
              {isReadOnlyMode ? (
                currentStep < 4 && (
                  <Button
                    onClick={handleNext}
                    className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium order-1 sm:order-2"
                  >
                    {currentStep === 3 ? 'Přejít na shrnutí' : 'Další'}
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                )
              ) : (
                // Edit mode: all original buttons
                <>
                  {currentStep === 2 && (
                    <Button
                      onClick={handleSaveForm}
                      disabled={!canProceed()}
                      className="w-full sm:w-auto bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium order-1 sm:order-2"
                    >
                      <Save size={16} className="mr-2" />
                      {t('form.save')}
                    </Button>
                  )}
                  
                  {currentStep < 2 && (
                    <Button
                      onClick={handleNext}
                      disabled={!canProceed()}
                      className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium order-1 sm:order-2"
                    >
                      {t('form.next')}
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  )}

                  {currentStep === 3 && (
                    <Button
                      onClick={handleNext}
                      disabled={!isFormSaved}
                      className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-medium order-1 sm:order-2"
                    >
                      Přejít na shrnutí
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Side Panel */}
      <SidePanel
        isOpen={isSidePanelOpen}
        onToggle={() => setIsSidePanelOpen(!isSidePanelOpen)}
        isFormSaved={isFormSaved}
        isSaving={isSaving}
        lastSaved={lastSaved}
        onContinueForm={(formId) => {
          navigate(`/form?formId=${formId}`);
        }}
      />
    </div>
  );
}